import React, { useState } from "react";
import { Box, Button, Typography, IconButton, styled } from "@mui/material";
import { AHSHeader } from "./AHSHeader";
import { AHSCoverContent } from "./AHSCoverContent";
import { AHSCompletionContent } from "./AHSCompletionContent";
import { RouteCard } from "./RouteCard";
import { BlurbDialog } from "./BlurbDialog";
import { HatIcon } from "../../icons/HatIcon";
import { AFTER_HIGH_SCHOOL_XP } from "../../../constants";
import type {
  AfterHighSchoolActivityProps,
  AfterHighSchoolBlurb,
} from "../../../types";
import bgMapImport from "./assets/bg-map.png";

// SVG/image imports resolve as strings (Vite) or { src } objects (Next.js)
const toSrc = (imported: string | { src: string }): string =>
  typeof imported === "string" ? imported : imported.src;

const bgMapSrc = toSrc(bgMapImport as string | { src: string });

const ActivityContainer = styled(Box, {
  label: "AHSActivityContainer",
})(({ theme }) => ({
  position: "fixed",
  margin: 0,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "#1c252e",
  zIndex: theme.zIndex.modal,
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  overflowY: "auto",
}));

const BackgroundMap = styled("img", {
  label: "AHSBackgroundMap",
})({
  position: "absolute",
  bottom: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: "100%",
  maxWidth: 1232,
  opacity: 0.5,
  pointerEvents: "none",
  userSelect: "none",
});

const ContentArea = styled(Box, {
  label: "AHSContentArea",
})(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(4),
  alignItems: "center",
  paddingTop: theme.spacing(5),
  paddingBottom: theme.spacing(6),
  paddingLeft: theme.spacing(6),
  paddingRight: theme.spacing(6),
  position: "relative",
  width: "100%",
}));

const CardsRow = styled(Box, {
  label: "AHSCardsRow",
})(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(9),
  alignItems: "flex-start",
}));

const NavigationSection = styled(Box, {
  label: "AHSNavigationSection",
})(() => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  width: "100%",
  maxWidth: 1280,
  margin: "0 auto",
  padding: "0 48px 48px",
}));

const CompleteButton = styled(Button, {
  label: "AHSCompleteButton",
})(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#ffffff",
  fontFamily: "Proxima Nova",
  fontWeight: 700,
  fontSize: "15px",
  lineHeight: "26px",
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.spacing(1),
  minWidth: 64,
  height: 48,
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const BlurbCard = styled(Box, {
  label: "AHSBlurbCard",
})(({ theme }) => ({
  backgroundColor: "#fcf2f2",
  borderRadius: 24,
  padding: theme.spacing(2, 3),
  display: "flex",
  gap: theme.spacing(3),
  alignItems: "flex-start",
  maxWidth: 559,
  width: "100%",
  cursor: "pointer",
  boxShadow:
    "0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px 0px rgba(145, 158, 171, 0.12)",
  transition: "box-shadow 0.2s",
  "&:hover": {
    boxShadow:
      "0px 0px 2px 0px rgba(145, 158, 171, 0.3), 0px 12px 24px 0px rgba(145, 158, 171, 0.2)",
  },
}));

const CarouselContainer = styled(Box, {
  label: "AHSCarouselContainer",
})(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  maxWidth: 663,
  width: "100%",
}));

const CarouselButton = styled(IconButton, {
  label: "AHSCarouselButton",
})(() => ({
  backgroundColor: "#f4f6f8",
  borderRadius: 10,
  width: 36,
  height: 36,
  "&:hover": {
    backgroundColor: "#e0e3e7",
  },
  "&.Mui-disabled": {
    backgroundColor: "#f4f6f8",
    opacity: 0.65,
  },
}));

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M12.5 15L7.5 10L12.5 5"
      stroke="#637381"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M7.5 15L12.5 10L7.5 5"
      stroke="#637381"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type Screen = "start" | "activity" | "complete";

export const AfterHighSchoolActivity = ({
  hat,
  onClose,
  activityCompletion,
  onComplete,
  onXPEarned,
}: AfterHighSchoolActivityProps) => {
  const [currentScreen, setCurrentScreen] = useState<Screen>("start");
  const [selectedBlurb, setSelectedBlurb] =
    useState<AfterHighSchoolBlurb | null>(null);
  const [blurbIndex, setBlurbIndex] = useState(0);
  const xpValue = AFTER_HIGH_SCHOOL_XP;

  const popularPaths = hat.msPopularPaths || [];
  const otherRoutes = hat.msOtherRoutes || [];
  const blurbs = hat.msAfterHighSchoolBlurbs || [];

  const handleStart = () => {
    setCurrentScreen("activity");
  };

  const handleGotIt = () => {
    if (!activityCompletion) {
      onComplete?.();
      onXPEarned?.(xpValue, `afterhighschool-${hat.id}`);
    }
    setCurrentScreen("complete");
  };

  const handleContinue = () => {
    onClose();
  };

  const currentBlurb = blurbs[blurbIndex] || null;

  const handlePrevBlurb = () => {
    if (blurbIndex > 0) setBlurbIndex(blurbIndex - 1);
  };

  const handleNextBlurb = () => {
    if (blurbIndex < blurbs.length - 1) setBlurbIndex(blurbIndex + 1);
  };

  if (currentScreen === "start") {
    return (
      <ActivityContainer>
        <AHSHeader onClose={onClose} hat={hat} />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 3,
          }}
        >
          <AHSCoverContent
            hat={hat}
            onStart={handleStart}
            activityCompletion={activityCompletion}
            xpValue={xpValue}
          />
        </Box>
      </ActivityContainer>
    );
  }

  if (currentScreen === "complete") {
    return (
      <ActivityContainer>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 3,
          }}
        >
          <AHSCompletionContent
            hat={hat}
            onContinue={handleContinue}
            xpValue={xpValue}
          />
        </Box>
      </ActivityContainer>
    );
  }

  return (
    <ActivityContainer>
      <BackgroundMap src={bgMapSrc} alt="" aria-hidden />
      <AHSHeader onClose={onClose} hat={hat} />

      <ContentArea>
        <CardsRow>
          <RouteCard
            title="Popular Paths"
            subtitle="Many people have taken these paths to this career."
            items={popularPaths}
            headerColor="#8e33ff"
            emptyMessage="There is no typical education path to this Hat."
          />

          <RouteCard
            title="Other Routes"
            subtitle="Less-traveled routes to this career."
            items={otherRoutes}
            headerColor="#003768"
            emptyMessage="The popular paths are the only realistic education and training options."
          />
        </CardsRow>

        {blurbs.length > 0 && (
          <CarouselContainer
            sx={blurbs.length === 1 ? { justifyContent: "center" } : undefined}
          >
            {blurbs.length > 1 && (
              <CarouselButton
                onClick={handlePrevBlurb}
                disabled={blurbIndex === 0}
              >
                <ChevronLeft />
              </CarouselButton>
            )}

            <BlurbCard onClick={() => setSelectedBlurb(currentBlurb)}>
              <Box component="span" sx={{ display: "flex", flexShrink: 0 }}>
                <HatIcon />
              </Box>
              <Typography
                sx={{
                  fontFamily: "Proxima Nova",
                  fontWeight: 600,
                  fontSize: "18px",
                  lineHeight: "28px",
                  color: "#1c252e",
                  flex: 1,
                }}
              >
                {currentBlurb?.title}
              </Typography>
            </BlurbCard>

            {blurbs.length > 1 && (
              <CarouselButton
                onClick={handleNextBlurb}
                disabled={blurbIndex === blurbs.length - 1}
              >
                <ChevronRight />
              </CarouselButton>
            )}
          </CarouselContainer>
        )}
      </ContentArea>

      <NavigationSection>
        <CompleteButton variant="contained" onClick={handleGotIt}>
          Complete
        </CompleteButton>
      </NavigationSection>

      <BlurbDialog
        open={selectedBlurb !== null}
        onClose={() => setSelectedBlurb(null)}
        blurb={selectedBlurb}
      />
    </ActivityContainer>
  );
};
